// src/App.jsx
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import AppShell, { preloadRoute } from './shell/AppShell';
import ChunkErrorBoundary     from './components/ChunkErrorBoundary';
import FeedbackWidget         from './shell/FeedbackWidget';

// 47070 B1 — the classic (?shell=off) app and its ~30 static page imports are a lazy chunk: the default shell never loads it.
const MainApp = React.lazy(() => import('./LegacyApp'));
const LegacyLoading = () => { const { t } = useTranslation(); return <div style={{ padding: '20px', textAlign: 'center' }}>{t('state.loading')}</div>; };

export default function App() {
  // v0.43 Step 0: dark by default. The legacy state-machine branch below remains
  // byte-for-byte isolated until this preview flag is explicitly enabled.
  const shellV2Enabled = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get('shell');
      // v0.43 cutover: shell v2 is the DEFAULT. Classic is a sticky opt-out via ?shell=off.
      if (q === 'v2')  { window.localStorage.removeItem('shell_v2'); return true; }   // clear any opt-out → v2
      if (q === 'off') { window.localStorage.setItem('shell_v2', 'off'); return false; }   // sticky opt-out to classic
      return window.localStorage.getItem('shell_v2') !== 'off';   // v2 by default; classic only when explicitly pinned off
    } catch (_) {
      return new URLSearchParams(window.location.search).get('shell') !== 'off';
    }
  })();
  // Round 4c: a signed-in reload starts the page chunk now, in parallel with the auth chain (AuthProvider fires its three reads together).
  try { if (shellV2Enabled && window.localStorage.getItem('accessToken')) preloadRoute(window.location.pathname === '/' ? '/home' : window.location.pathname); } catch (_) { /* best effort */ }
  const shell = <ProtectedRoute><ChunkErrorBoundary><AppShell /></ChunkErrorBoundary></ProtectedRoute>;
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {shellV2Enabled ? <>
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* ONE catch-all so AppShell stays mounted across every shell navigation — it parses the
                path itself (useLocation). Separate per-depth routes used to remount it on each segment
                change, which re-fetched the sanity/clean-date banners every time. */}
            <Route path="/*" element={shell} />
          </> : <Route path="/*" element={<ProtectedRoute><ChunkErrorBoundary><Suspense fallback={<LegacyLoading />}><MainApp /></Suspense></ChunkErrorBoundary></ProtectedRoute>} />}
        </Routes>
        <FeedbackWidget />
      </AuthProvider>
    </BrowserRouter>
  );
}
